import { MecordColors } from "Constants/Color";
import React from "react";
import { Link } from "react-router-dom";

interface ActionButtonProps {
  label: string; // Button label text
  icon?: string | React.ReactNode; // Optional icon class (e.g., "bi bi-plus-circle")
  handleClick?: () => void; // Optional click handler function
  navigateLink?: string; // Optional navigation link
  variant?:
    | "primary"
    | "light"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "info"; // Button variant
  className?: string; // Additional CSS classes
  disabled?: boolean; // Disabled state
  style?: React.CSSProperties; // Additional inline styles
}

const ActionButton: React.FC<ActionButtonProps> = ({
  label,
  icon,
  handleClick,
  navigateLink,
  variant = "primary",
  className = "",
  disabled = false,
  style,
}) => {
  const themeColor = MecordColors?.ThemeBGColor; // Primary color from tableStyles (same as SubmitButton)

  const baseClasses =
    variant === "primary"
      ? `btn custom-action-button ${className}`.trim()
      : `btn btn-${variant} ${className}`.trim();

  // const iconClasses = icon ? `${icon} align-baseline me-1` : "";
  const combinedClasses = icon
    ? `hstack gap-2 ${baseClasses}`.trim()
    : baseClasses;

  // Apply theme color for primary variant
  const buttonStyle: React.CSSProperties =
    variant === "primary"
      ? {
          backgroundColor: themeColor,
          borderColor: themeColor,
          color: "#fff",
          ...style,
        }
      : style || {};

  const renderIcon = () => {
    if (!icon) return null;

    // String-based icon (Bootstrap, FontAwesome, etc.)
    if (typeof icon === "string") {
      return <i className={`${icon} align-baseline me-1`} />;
    }

    // React component (MUI icons, SVGs, etc.)
    return (
      <span className="me-1 d-inline-flex align-items-center">{icon}</span>
    );
  };

  const buttonContent = (
    <>
      {/* {icon && <i className={iconClasses}></i>} */}
      {renderIcon()}
      {label}
    </>
  );

  return (
    <>
      {variant === "primary" && (
        <style>
          {`
            .custom-action-button {
              background-color: ${themeColor} !important;
              border-color: ${themeColor} !important;
              color: #fff !important;
            }
            .custom-action-button:hover {
              background-color: ${themeColor} !important;
              border-color: ${themeColor} !important;
              opacity: 0.9;
            }
            .custom-action-button:focus {
              background-color: ${themeColor} !important;
              border-color: ${themeColor} !important;
              box-shadow: 0 0 0 0.2rem ${themeColor}40 !important;
            }
            .custom-action-button:disabled {
              background-color: ${themeColor} !important;
              border-color: ${themeColor} !important;
              opacity: 0.6;
            }
          `}
        </style>
      )}
      {/* If navigateLink is provided, render as Link */}
      {navigateLink ? (
        <Link
          to={navigateLink}
          className={combinedClasses}
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            ...buttonStyle,
          }}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
            } else if (handleClick) {
              e.preventDefault();
              handleClick();
            }
          }}
        >
          {buttonContent}
        </Link>
      ) : handleClick ? (
        // Otherwise, render as button or div (for onClick)
        <div
          className={combinedClasses}
          onClick={disabled ? undefined : handleClick}
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            ...buttonStyle,
          }}
        >
          {buttonContent}
        </div>
      ) : (
        // Fallback: render as button element
        <button
          type="button"
          className={combinedClasses}
          disabled={disabled}
          style={buttonStyle}
        >
          {buttonContent}
        </button>
      )}
    </>
  );
};

export default ActionButton;
